export async function verifyExchange(ctx, accessToken) {
    try {
        const response = await fetch(`${ctx.apiBaseUrl}/api/v1/exchange/verify`, {
            method: "POST",
            headers: ctx.headers,
            body: JSON.stringify({
                authToken: accessToken,
                accountInfo: ["email", "firstName", "lastName"],
            }),
        });
        const body = await response.json();
        if (response.ok && body?.status === "ok" && body?.content) {
            return {
                email: body.content.email ?? null,
                firstName: body.content.firstName ?? null,
                lastName: body.content.lastName ?? null,
            };
        }
        console.warn("[MeshProvider] Verify returned non-ok:", body?.message ?? body?.errorType);
        return { email: null, firstName: null, lastName: null };
    }
    catch (error) {
        console.warn("[MeshProvider] Failed to verify account:", error);
        return { email: null, firstName: null, lastName: null };
    }
}
export async function refreshAccessToken(ctx, refreshToken, brokerType) {
    try {
        const response = await fetch(`${ctx.apiBaseUrl}/api/v1/token/refresh`, {
            method: "POST",
            headers: ctx.headers,
            body: JSON.stringify({ refreshToken, type: brokerType }),
        });
        const body = await response.json();
        if (!response.ok || body?.status !== "ok" || !body?.content?.accessToken) {
            console.error("[MeshProvider] Token refresh failed:", body?.message ?? body?.errorType);
            return null;
        }
        return {
            accessToken: body.content.accessToken,
            refreshToken: body.content.refreshToken,
            expiresInSeconds: body.content.expiresInSeconds,
            refreshTokenExpiresInSeconds: body.content.refreshTokenExpiresInSeconds,
        };
    }
    catch (error) {
        console.error("[MeshProvider] Failed to refresh token:", error);
        return null;
    }
}
function parseMarketValueUsd(position) {
    if (typeof position?.marketValue === "number")
        return position.marketValue;
    if (typeof position?.marketValueUsd === "number")
        return position.marketValueUsd;
    return undefined;
}
export async function fetchBrokerHoldings(ctx, params) {
    const { accessToken, brokerType, brokerName, accountAddress, accountKey } = params;
    try {
        const requestBody = { authToken: accessToken };
        if (brokerType) {
            requestBody.type = brokerType;
        }
        const response = await fetch(`${ctx.apiBaseUrl}/api/v1/holdings/get`, {
            method: "POST",
            headers: ctx.headers,
            body: JSON.stringify(requestBody),
        });
        if (!response.ok) {
            const errorBody = await response.text();
            let parsed = null;
            try {
                parsed = errorBody ? JSON.parse(errorBody) : null;
            }
            catch {
                // ignore
            }
            console.warn(`[MeshProvider] Failed holdings request for ${accountKey}: ${response.status}`, parsed?.message ?? parsed?.errorType ?? errorBody);
            return null;
        }
        const body = await response.json();
        if (body?.status !== "ok" || !body?.content) {
            console.warn(`[MeshProvider] Invalid holdings response for ${accountKey}`);
            return null;
        }
        const positions = (body.content?.cryptocurrencyPositions ?? [])
            .map((position) => ({
            symbol: String(position?.symbol ?? ""),
            amount: Number(position?.amount ?? 0),
            marketValueUsd: parseMarketValueUsd(position),
        }))
            .filter((position) => !!position.symbol && Number.isFinite(position.amount));
        return {
            accountAddress,
            brokerType,
            brokerName,
            positions,
        };
    }
    catch (error) {
        console.warn(`[MeshProvider] Failed to fetch holdings for ${accountKey}:`, error);
        return null;
    }
}
export async function previewTransfer(ctx, params) {
    const previewResponse = await fetch(`${ctx.apiBaseUrl}/api/v1/transfers/managed/preview`, {
        method: "POST",
        headers: ctx.headers,
        body: JSON.stringify({
            fromAuthToken: params.accessToken,
            fromType: params.brokerType,
            symbol: params.symbol,
            amount: params.amount,
            toAddress: params.toAddress,
            networkId: params.networkId,
        }),
    });
    const previewBody = await previewResponse.json();
    if (!previewResponse.ok || previewBody?.status !== "ok") {
        const msg = previewBody?.message ?? previewBody?.displayMessage ?? "Transfer preview failed";
        throw new Error(msg);
    }
    const previewId = previewBody.content?.previewResult?.previewId;
    if (!previewId) {
        throw new Error("Your exchange could not complete the transfer. Please try again.");
    }
    return previewId;
}
export function mapTransferStatus(status) {
    const s = status ?? "";
    if (/^processing$/i.test(s) || /^in_progress$/i.test(s))
        return "processing";
    if (/^completed$/i.test(s) || /^success$/i.test(s))
        return "completed";
    if (/^failed$/i.test(s) || /^error$/i.test(s))
        return "failed";
    return "pending";
}
/**
 * Parse a successful execute response body into a typed result.
 * Extracted to reduce the complexity of executeTransfer.
 */
function parseExecuteBody(body) {
    const content = body.content;
    if (!content) {
        return { kind: "error", message: body.message ?? "Transfer failed" };
    }
    if (content.status === "mfaRequired") {
        return { kind: "mfaRequired", mfaType: content.mfaType ?? "totp" };
    }
    const executeResult = (content.executeTransferResult ?? content);
    const transferId = executeResult?.transferId ?? executeResult?.id ?? content?.transferId ?? content?.id;
    if (!transferId) {
        return buildMissingTransferIdError(content, executeResult, body);
    }
    return {
        kind: "ok",
        transferId: String(transferId),
        txHash: (executeResult?.txHash ?? executeResult?.hash) ?? undefined,
        status: mapTransferStatus(executeResult?.status),
    };
}
function buildMissingTransferIdError(content, executeResult, body) {
    const status = (content.status ?? executeResult?.status);
    const msg = (content.errorMessage ?? body.message ?? body.displayMessage);
    console.warn("[MeshProvider] Transfer ID missing from Mesh response", {
        contentStatus: status,
        message: msg,
        contentKeys: Object.keys(content),
    });
    let userMessage = "Your exchange could not complete the transfer. Please try again.";
    if (msg) {
        userMessage = `Your exchange reported: ${msg}`;
    }
    else if (status === "mfaFailed") {
        userMessage = "Your exchange could not complete the transfer. Verification code was incorrect. Please try again.";
    }
    return { kind: "error", message: userMessage };
}
export async function executeTransfer(ctx, params) {
    const executePayload = {
        fromAuthToken: params.accessToken,
        fromType: params.brokerType,
        previewId: params.previewId,
    };
    if (params.mfaCode) {
        executePayload.mfaCode = params.mfaCode;
    }
    const response = await fetch(`${ctx.apiBaseUrl}/api/v1/transfers/managed/execute`, {
        method: "POST",
        headers: ctx.headers,
        body: JSON.stringify(executePayload),
    });
    const body = await response.json();
    if (!response.ok) {
        const errorMessage = body?.message ?? body?.displayMessage ?? `Transfer request failed (${response.status})`;
        return { kind: "error", message: errorMessage };
    }
    if (body?.status !== "ok" || !body?.content) {
        return { kind: "error", message: body?.message ?? "Transfer failed" };
    }
    return parseExecuteBody(body);
}
